/**
 * «Локальный Клод» — разговор со СВОИМ Claude Code через мост `cubus-claude-bridge`.
 *
 * ЧЕМ ЭТОТ РЕЖИМ ОТЛИЧАЕТСЯ ОТ ДЕМОНСТРАЦИОННОГО АГЕНТА. Демо-агент идёт `браузер → бэкенд Cubus
 * → раннер`, и Cubus знает о разговоре всё: ведёт историю, журнал заданий, выдаёт агенту доступ.
 * Локальный режим идёт мимо бэкенда: Клод работает на машине человека, под его подпиской и в его
 * папке. Отсюда всё остальное — и то, что ответ не ложится в историю чата Cubus, и то, что мост
 * надо поставить самому.
 *
 * ⚠️ ПОЧЕМУ ЗАПРОС ИДЁТ ЧЕРЕЗ ОКНО, А НЕ `fetch` (BRG-417). Замеры 08.09 в настоящих браузерах:
 *
 *   |            | fetch → http | fetch → https | переход в окно |
 *   |------------|--------------|---------------|----------------|
 *   | Safari 26  | ❌ mixed content | ✅            | ✅              |
 *   | Chrome 152 | ❌ висит молча   | ❌ висит молча | ✅              |
 *
 * То есть сертификат на 127.0.0.1 вылечил бы Safari и НЕ вылечил бы Chrome: его проверка доступа
 * к локальной сети от схемы не зависит. А навигация проходит в обоих. Поэтому мост сам отдаёт
 * страницу-посредник по адресу `/client`, Cubus открывает её окном, и запросы ретранслируются
 * через `postMessage`. Ни смешанное содержимое, ни проверка локальной сети к этому не относятся.
 *
 * ⚠️ ТОКЕН МОСТА СЮДА НЕ ПОПАДАЕТ ВОВСЕ. Он живёт в `localStorage` адреса моста, у страницы-
 * посредника, и странице Cubus не виден ни в каком виде. Это лучше прежнего порядка, где человек
 * вставлял токен руками: мост по своему назначению ВЫПОЛНЯЕТ КОД на машине (`Bash` разрешён,
 * правки без подтверждения), и его токен опаснее токена Cubus, который из хранилища убрали
 * (PROP-086). Теперь доступ выдаётся связыванием: человек подтверждает папку и срок в системном
 * окне на своей машине, а не переносит секрет через буфер обмена.
 */

/* Поле `__tag` — часть протокола моста, имя задано не здесь. */
/* eslint-disable no-underscore-dangle */

import axiosIns, { getConfig } from '@axios'

const URL_KEY = 'cubus.bridge.url'
export const DEFAULT_BRIDGE_URL = 'http://127.0.0.1:3777'

const TAG = 'cubus-bridge-1'
/**
 * Сколько ждём признаков жизни от окна моста, когда мост на этой же машине. Дальше — «мост не
 * отвечает»: локальный мост либо здоровается сразу, либо не запущен, и ждать дольше нечего.
 */
const READY_TIMEOUT_MS = 8000
/**
 * То же для публичного адреса (BRG-473). Снаружи перед окном моста встаёт вход Cloudflare Access,
 * и ждём мы не загрузку страницы, а ЧЕЛОВЕКА: открыть почту, найти письмо, ввести код. С прежними
 * восемью секундами это заканчивалось отказом «мост не отвечает, проверьте claude-bridge status» —
 * то есть человека отправляли чинить исправную службу, пока форма входа висела у него перед носом.
 */
const READY_TIMEOUT_REMOTE_MS = 180000
/** Сколько ждём ответа человека в системном окне согласия. У самого моста там 120 с. */
const CONSENT_TIMEOUT_MS = 180000
/** Как часто смотрим, не закрыл ли человек окно моста, пока мы его ждём. */
const CLOSED_POLL_MS = 1000

export function getBridgeUrl() {
  try {
    return localStorage.getItem(URL_KEY) || DEFAULT_BRIDGE_URL
  } catch (e) {
    // Приватное окно и запрещённые куки: адрес по умолчанию лучше отказа.
    return DEFAULT_BRIDGE_URL
  }
}

export function setBridgeUrl(value) {
  const url = String(value || '').trim() || DEFAULT_BRIDGE_URL
  try { localStorage.setItem(URL_KEY, url) } catch (e) { /* не критично */ }
  return url
}

const origin = url => {
  try { return new URL(url).origin } catch (e) { return '' }
}

/**
 * Мост на этой же машине? От этого зависит и срок ожидания, и текст отказа: «проверьте
 * claude-bridge status» осмысленно только тогда, когда терминал с мостом — под рукой.
 */
const isLocalBridge = url => {
  try {
    const h = new URL(url).hostname.replace(/^\[|\]$/g, '')
    return h === 'localhost' || h === '::1' || /^127\./.test(h)
  } catch (e) { return false }
}

/** Origin API Cubus — он же адрес инструментов MCP. */
const apiOrigin = () => origin(getConfig() || '')

/* --- состояние соединения ------------------------------------------------- */

/** Окно-посредник, отданное самим мостом. */
let win = null
/** Origin моста, с которым связано это окно. */
let peerOrigin = ''
/** Текущее соединение: `{ folder, expiresAt, apiOrigin }` либо `null`. */
let connection = null
let nextId = 1
/** id запроса → `{ resolve, reject }`. */
const pending = new Map()
const listeners = new Set()
let watchdog = null

const CLOSED = 'Окно моста закрыто — доступ прекращён. Нажмите «Подключить», чтобы вернуть его.'

export function bridgeConnection() { return connection }

/**
 * BFILE-558: умеет ли ЭТОТ мост передавать файлы И разрешил ли человек.
 *
 * Два условия проверяются раздельно, хотя мост сообщает возможность только при разрешении:
 * это разные данные (умение — про сборку, разрешение — про человека), и сведи мы их в одно,
 * первая же расхождение показало бы человеку скрепку, которая отвечает отказом.
 */
export function bridgeCanFiles() {
  return !!connection && connection.features.indexOf('files') >= 0 && connection.files === true
}

/**
 * BFILE-564 (ТЗ моста §6.2): умеет ли мост запускать прогон с памятью без настроек, скиллов и
 * хуков человека. Старый мост признак `memoryRun` молча проигнорирует — поэтому признак шлётся
 * только тому, кто объявил возможность.
 */
export function bridgeCanMemory() {
  return !!connection && connection.features.indexOf('memory') >= 0
}

/** Номер сборки моста — для подсказки «обновите мост»; пусто у мостов до BFILE-551. */
export function bridgeBuild() { return connection ? connection.build : '' }

/**
 * RFILE-630 (ТЗ «Мост: файлы результата» §8 п. 1): умеет ли мост отдавать созданные агентом файлы И
 * разрешил ли это человек третьим вопросом при связывании. Старый мост возможности не назовёт — поле
 * `resultFiles` ему не шлётся вовсе (§8 п. 2).
 */
export function bridgeCanResultFiles() {
  return !!connection && connection.features.indexOf('result_files') >= 0 && connection.resultFiles === true
}

/** RFILE-630: разрешил ли человек отправку созданных файлов этому доступу (поле гранта). */
export function bridgeResultFilesAllowed() { return !!connection && connection.resultFiles === true }

/**
 * BUG-641: знает ли мост о файлах результата вообще — прислал ли поле `resultFiles` при подключении.
 *
 * Мост выпуска шлёт поле всегда (`false` — человек не разрешил, в том числе грант прежней сборки), мост до
 * выпуска не шлёт его вовсе. Сведённые в одно `false`, эти случаи давали прежнему мосту совет «разрешите
 * третьим вопросом» — а он третьего вопроса не задаёт, и человек ходил по кругу.
 */
export function bridgeKnowsResultFiles() { return !!connection && connection.resultFiles !== null }

/** Разрешил ли человек файлы этому доступу (поле гранта, а не умение сборки). */
export function bridgeFilesAllowed() { return !!connection && connection.files === true }
export function isBridgeConnected() { return !!connection && !!win && !win.closed }

/** Подписка на изменения соединения: панель показывает состояние, а не гадает. */
export function onBridgeChange(cb) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
/**
 * Слушателям сообщается и ПРИЧИНА: «отключено» по нажатию человека и «отключено», потому что
 * окно закрыли, — для панели это разные события. Без причины сознательное отключение
 * показывалось бы человеку как поломка.
 */
const announce = why => listeners.forEach(cb => {
  // Неисправный слушатель не должен ронять соединение.
  try { cb(connection, why) } catch (e) { /* пропускаем */ }
})

function dropConnection(message, why) {
  // Идемпотентно: отключение приходит дважды — от нас и ответным «revoked» из окна.
  if (!connection && pending.size === 0 && !watchdog) return
  connection = null
  pending.forEach(({ reject }) => reject(new Error(message)))
  pending.clear()
  if (watchdog) {
    clearInterval(watchdog)
    watchdog = null
  }
  announce(why)
}

function post(msg) {
  if (!win || win.closed) throw new Error(CLOSED)
  // targetOrigin НИКОГДА не '*': сообщение адресовано мосту, и уехать оно должно только ему.
  win.postMessage({ ...msg, __tag: TAG }, peerOrigin)
}

/**
 * Единственный слушатель на модуль. Источник проверяется дважды: и по окну
 * (`e.source`), и по origin — окно могло уйти на другой адрес.
 */
function handleMessage(e) {
  if (!win || e.source !== win || e.origin !== peerOrigin) return
  const d = e.data
  if (!d || d.__tag !== TAG) return

  // BRG-438: веха хода работы. Приходит между запросом и ответом, сколько угодно раз, и
  // запрос НЕ закрывает — закрывает его по-прежнему «response».
  if (d.type === 'event') {
    const waiter = pending.get(d.id)
    if (waiter && waiter.onEvent) {
      try { waiter.onEvent(d.event, d.data) } catch (err) { /* обработчик вех не вправе рвать запрос */ }
    }
    return
  }
  if (d.type === 'response') {
    const waiter = pending.get(d.id)
    if (!waiter) return
    pending.delete(d.id)
    waiter.resolve({ status: d.status, body: d.body })
    return
  }
  // RFILE-631: байты файла результата от окна — Blob ссылкой или код отказа моста.
  if (d.type === 'resultFileData') {
    const waiter = pending.get(d.id)
    if (!waiter) return
    pending.delete(d.id)
    waiter.resolve({
      status: d.status, blob: d.blob || null, name: d.name || '', error: d.error || null,
    })
    return
  }
  if (d.type === 'revoked') {
    dropConnection('Доступ к папке отозван.', 'revoked')
  }
}

if (typeof window !== 'undefined') window.addEventListener('message', handleMessage)

/**
 * Подключиться: открыть окно моста и дождаться, пока человек подтвердит доступ.
 *
 * Окно нужно открывать ПРЯМО в обработчике нажатия — иначе браузер сочтёт его
 * непрошеным и заблокирует.
 */
export function connectBridge() {
  const base = getBridgeUrl().replace(/\/+$/, '')
  peerOrigin = origin(base)
  if (!peerOrigin) return Promise.reject(new Error('Адрес моста не разбирается как адрес.'))

  if (win && !win.closed && connection) {
    win.focus()
    return Promise.resolve(connection)
  }
  /**
   * Окно живо, но связывания нет — это брошенная попытка: вход Access не довели до конца либо
   * системное окно согласия осталось без ответа. Переиспользовать такое окно нельзя: приветствие
   * оно шлёт РОВНО ОДИН РАЗ при загрузке, и второго мы не дождёмся — «Подключить» упиралось бы в
   * тот же отказ, сколько ни нажимай. Поэтому закрываем и открываем заново (BRG-473).
   */
  if (win && !win.closed) {
    try { win.close() } catch (e) { /* уже закрыто */ }
  }
  win = window.open(`${base}/client`, 'cubus-bridge', 'width=460,height=340')

  if (!win) {
    return Promise.reject(new Error(
      'Браузер заблокировал окно моста. Разрешите всплывающие окна для этого сайта и нажмите «Подключить» ещё раз.',
    ))
  }

  return new Promise((resolve, reject) => {
    let ready = false
    let done = false
    let readyTimer = null
    let consentTimer = null
    let closedWatch = null
    let onMsg = null
    const local = isLocalBridge(base)
    const finish = (fn, arg) => {
      if (done) return
      done = true
      window.removeEventListener('message', onMsg)
      clearTimeout(readyTimer)
      clearTimeout(consentTimer)
      clearInterval(closedWatch)
      fn(arg)
    }

    onMsg = e => {
      if (!win || e.source !== win || e.origin !== peerOrigin) return
      const d = e.data
      if (!d || d.__tag !== TAG) return
      if (d.type === 'ready') {
        ready = true
        clearTimeout(readyTimer)
        /**
         * Срок согласия идёт ОТСЮДА, а не от открытия окна (BRG-473): до приветствия мы ждём
         * загрузку и вход, а окно согласия на машине к тому времени ещё не показывали. Отсчитывай
         * мы от открытия, долгий вход снаружи съедал бы бюджет, отведённый человеку у самого окна.
         */
        consentTimer = setTimeout(() => {
          finish(reject, new Error('Подтверждения доступа не было — окно согласия осталось без ответа.'))
        }, CONSENT_TIMEOUT_MS)
        // Кто мы — окно узнаёт из ОТВЕТА на приветствие: адресу в строке доверия нет.
        /**
         * Вместе с приветствием называем адрес API: у Cubus страница и API живут на разных
         * хостах, и мост должен знать, куда этой странице разрешено направлять инструменты
         * агента. Разрешает не она — человек видит адрес в окне согласия.
         */
        post({ type: 'hello', apiOrigin: apiOrigin() })
        return
      }
      if (d.type === 'connected') {
        // BRG-437: apiOrigin — то, что подтвердил МОСТ. Пусто значит «инструменты этим
        // доступом не разрешены», и говорить обратное панель не вправе.
        /**
         * BFILE-558 (ТЗ моста §13 пп. 1, 2): вместе с папкой запоминаем ВОЗМОЖНОСТИ моста.
         *
         * Мосты стоят на машинах людей и обновляются вразнобой — порядок выкатки здесь не
         * работает, работает только договорённость о возможностях. Старый мост поля `features`
         * не пришлёт вовсе, и его отсутствие и есть ответ «файлов не умею»; номер сборки нужен,
         * чтобы человеку было что назвать, когда мост просят обновить.
         */
        connection = {
          folder: d.folder,
          expiresAt: d.expiresAt,
          apiOrigin: d.apiOrigin || '',
          features: Array.isArray(d.features) ? d.features : [],
          files: d.files === true,
          // RFILE-630: третий вопрос — отправка созданных файлов в Cubus. BUG-641: `null` — мост поля не прислал
          // (сборка до выпуска), и это не то же, что «не разрешено».
          resultFiles: typeof d.resultFiles === 'boolean' ? d.resultFiles : null,
          build: typeof d.build === 'string' ? d.build : '',
        }
        announce('connected')
        // Окно и есть канал: закрыли — доступа больше нет, и человек должен это увидеть.
        if (watchdog) clearInterval(watchdog)
        watchdog = setInterval(() => { if (!win || win.closed) dropConnection(CLOSED, 'closed') }, 1000)
        finish(resolve, connection)
        return
      }
      if (d.type === 'denied') {
        finish(reject, new Error(d.code === 'CONSENT_DENIED'
          ? 'Доступ не выдан: подтверждения не было.'
          : 'Мост не выдал доступ.'))
      }
    }

    window.addEventListener('message', onMsg)
    readyTimer = setTimeout(() => {
      if (ready) return
      finish(reject, new Error(local
        ? `Мост по адресу ${base} не отвечает. Проверьте, что он запущен: «claude-bridge status» в терминале.`
        : `Окно моста по адресу ${base} так и не открылось. Если в нём висит вход — завершите его `
          + 'и нажмите «Подключить» ещё раз. Если страница не загрузилась — проверьте сеть и то, '
          + 'что мост и туннель на вашей машине запущены.'))
    }, local ? READY_TIMEOUT_MS : READY_TIMEOUT_REMOTE_MS)
    /**
     * Человек может закрыть окно, не доведя дело до конца. Раньше это было незаметно — ждали
     * восемь секунд; с длинным сроком снаружи молчание растянулось бы на три минуты, поэтому
     * закрытие окна признаётся отказом сразу.
     */
    closedWatch = setInterval(() => {
      if (!win || win.closed) finish(reject, new Error('Окно моста закрыто — доступ не выдан.'))
    }, CLOSED_POLL_MS)
  })
}

/** Отключиться: грант отзывается на мосту, окно закрывается. */
export function disconnectBridge() {
  try { if (win && !win.closed) post({ type: 'unpair' }) } catch (e) { /* окна уже нет */ }
  // Отзыв на мосту делается страницей-посредником, поэтому даём ей мгновение
  // и только потом закрываем окно.
  setTimeout(() => { try { if (win && !win.closed) win.close() } catch (e) { /* уже закрыто */ } }, 300)
  dropConnection('Отключено.', 'manual')
}

/** Запрос к мосту через окно-посредник. */
function relay(path, { method = 'GET', body = null, onEvent = null } = {}) {
  if (!isBridgeConnected()) return Promise.reject(new Error(CLOSED))
  const id = nextId
  nextId += 1
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject, onEvent })
    try {
      post({
        type: 'request', id, method, path, body, stream: !!onEvent,
      })
    } catch (e) {
      pending.delete(id)
      reject(e)
    }
  })
}

/**
 * Человеческие объяснения кодов моста.
 *
 * «Установлен» и «готов работать» — разные вещи, и мост это различает четырьмя состояниями.
 * Пересказывать их сырым кодом значило бы отправить человека читать чужую документацию в тот
 * момент, когда ему нужно одно действие.
 */
const ERRORS = {
  UNAUTHORIZED: 'Мост не принял доступ — возможно, он истёк. Нажмите «Подключить» ещё раз.',
  CONSENT_DENIED: 'Доступ к папке не выдан.',
  FORBIDDEN_ORIGIN: 'Мост не пускает этот адрес. Добавьте его в «allowedOrigins» в настройках моста.',
  FORBIDDEN_HOST: 'Мост не принимает обращения по этому адресу: мост на этой машине отвечает только на «127.0.0.1» или «localhost», мост «наружу» — только на имени из «publicHostname» в его настройках.',
  UNKNOWN_PROJECT: 'Доступ выдан на одну папку — выбирать проект здесь не нужно.',
  PAYLOAD_TOO_LARGE: 'Запрос длиннее, чем мост готов принять.',
  QUEUE_TIMEOUT: 'Мост занят другим запросом и не дождался очереди.',
  CLAUDE_NOT_FOUND: 'На машине не найден Claude Code. Установите его либо укажите путь в «CLAUDE_BRIDGE_CLAUDE_BIN».',
  CLAUDE_NOT_AUTHENTICATED: 'Claude Code установлен, но вход не выполнен: «claude auth login».',
  CLAUDE_NO_PLAN: 'У аккаунта Claude нет активного плана — мост работает на вашей подписке.',
  CLAUDE_FAILED: 'Claude Code завершился с ошибкой.',
  RUN_TIMEOUT: 'Клод не уложился в отведённое мостом время.',
  OUTPUT_LIMIT: 'Ответ оказался больше, чем мост готов отдать.',
  CANCELLED: 'Запрос отменён.',
  NOT_CONNECTED: 'Окно моста не подключено — нажмите «Подключить».',
  RELAY_FAILED: 'Окно моста не смогло передать запрос.',
  INTERNAL: 'Внутренняя ошибка моста — смотрите его журнал «bridge.log».',
}

export function explainBridgeError(code, message) {
  // BRG-470: у FORBIDDEN_HOST мост сам называет, на какое имя отвечает, — это и есть подсказка.
  if (code === 'FORBIDDEN_HOST' && message) return `${ERRORS[code]} Мост: ${message}.`
  return ERRORS[code] || message || 'Мост ответил ошибкой.'
}

/**
 * PROP-472.1/472.3: расход и модель из ответа моста — для строки журнала `ai_activity`.
 * Нули читаются как «неизвестно»: событие могло не дойти до конца.
 */
function readUsage(src) {
  return {
    model: (src && src.model) || '',
    inputTokens: (src && src.inputTokens) || 0,
    outputTokens: (src && src.outputTokens) || 0,
    durationMs: (src && src.durationMs) || 0,
    /**
     * `num_turns` кладём в `steps`: у прогона CLI это ближайшее к «сколько шагов сделал
     * агент». Ноль означает «неизвестно», а не «не сделал ни шага», — так же это читает
     * серверный писатель журнала.
     */
    steps: (src && src.numTurns) || 0,
  }
}

function unwrap({
  status,
  body,
}) {
  if (status >= 200 && status < 300) return body
  const err = (body && body.error) || {}
  // BRG-435: код отказа несём вместе с текстом. Отмена — не поломка, и панели надо отличать
  // «человек нажал Прервать» от «мост упал», а по переведённой фразе этого не сделать.
  const e = new Error(explainBridgeError(err.code, err.message))
  e.code = err.code || ''
  // PROP-472.3: неудачный прогон тоже стоил токенов, и журнал его тоже ждёт.
  // Мост кладёт расход и в событие ошибки, поэтому здесь его есть откуда взять.
  e.usage = readUsage(err)
  /**
   * BFILE-561: момент окончания несёт и ОТКАЗ (`BFILE-555`). По неудачному прогону отчёт уходит
   * так же, и время в нём должно быть мостовым — иначе след закроется по часам чужой машины.
   */
  e.finishedAt = Number(err.finishedAt) || 0
  throw e
}

/**
 * BFILE-560 (ТЗ моста §4.2): байты файла — мосту, через то же окно-посредник.
 *
 * Отдельно от `relay` намеренно: тело здесь двоичное, и окно везёт его особым сообщением
 * (`BFILE-553`). `Blob` уезжает ССЫЛКОЙ — структурное клонирование его не копирует, и 50 МБ
 * доезжают за десятые доли секунды (проба `BFILE-540`).
 */
export function sendFileToBridge(meta, blob) {
  if (!isBridgeConnected()) return Promise.reject(new Error(CLOSED))
  const id = nextId
  nextId += 1
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject, onEvent: null })
    try {
      post({
        type: 'file',
        id,
        file: {
          id: meta.id, name: meta.name, size: meta.size, sha256: meta.sha256,
        },
        blob,
      })
    } catch (e) {
      pending.delete(id)
      reject(e)
    }
  })
}

/**
 * RFILE-631 (ТЗ «Мост: файлы результата» §5 п. 4): байты описанного мостом файла результата — через окно-посредник.
 *
 * Отдельное сообщение `resultFile`, а не `relay`: тот читает тело текстом, и двоичный файл приехал бы
 * испорченным молча (находка М1). Окно отдаёт Blob ссылкой (проба RFILE-619). Отказ моста (`404` — срок
 * папки истёк или мост перезапущен, `409 CHANGED` — файл изменился после проверки) — ошибкой с кодом.
 */
export function fetchResultFileFromBridge(requestId, sha256) {
  if (!isBridgeConnected()) return Promise.reject(new Error(CLOSED))
  const id = nextId
  nextId += 1
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject, onEvent: null })
    try {
      post({
        type: 'resultFile', id, requestId, sha256,
      })
    } catch (e) {
      pending.delete(id)
      reject(e)
    }
  }).then(r => {
    if (r.status === 200 && r.blob) return r.blob
    const code = (r.error && r.error.code) || ''
    const reasons = {
      CHANGED: 'файл изменился после проверки мостом',
      NOT_FOUND: 'мост его больше не хранит (срок истёк или мост перезапущен)',
    }
    const e = new Error(reasons[code] || (r.error && r.error.message) || `мост ответил ${r.status}`)
    e.code = code
    throw e
  })
}

/**
 * RFILE-631 (§2 п. 8): байты места — в Cubus по ПРОПУСКУ из ответа на отчёт. Токен агента к этому моменту
 * погашен отчётом, и сессия человека эту ручку для прогона не открывает: ключ — только пропуск.
 */
export async function uploadResultFileToCubus(pass, jobId, name, sha256, blob) {
  const form = new FormData()
  form.append('file', blob, name)
  form.append('job_id', jobId)
  form.append('sha256', sha256)
  /**
   * ⚠️ `fetch`, А НЕ ОБЩИЙ `axiosIns`: его перехватчик (`jwtService`) ставит `Authorization` токеном СЕССИИ
   * поверх любого переданного заголовка — пропуск не доходил, сервер видел сессию человека и отказывал
   * «у сессии человека файлов результата нет». Поймано браузерной приёмкой на стенде. Cookie не шлём: ключ здесь —
   * только пропуск.
   */
  let res
  try {
    res = await fetch(`${getConfig().replace(/\/+$/, '')}/api/agent/result-file`, {
      method: 'POST', headers: { Authorization: `Bearer ${pass}` }, body: form, credentials: 'omit',
    })
  } catch (e) {
    throw new Error((e && e.message) || 'сеть недоступна')
  }
  let data = null
  try { data = await res.json() } catch (e) { data = null }
  if (!res.ok) throw new Error((data && data.error) || `Cubus ответил ${res.status}`)
  return data
}

/**
 * Запасной путь: байтов в браузере нет (вкладку перезагрузили между прикреплением и отправкой).
 * Тогда страница скачивает файл САМА, своей сессией, — мост в Cubus по-прежнему не ходит.
 */
async function downloadAttachment(meta) {
  const res = await axiosIns.post(
    '/api/download',
    { param: { method: 'agentattachment', fileid: meta.id } },
    { responseType: 'blob' },
  )
  const data = res && res.data
  if (!data) throw new Error('пустой ответ')
  /**
   * Платформа отдаёт либо поток, либо ССЫЛКУ на хранилище (только для незашифрованного файла в
   * нешифрующем домене). Ссылку отличаем по типу ответа: json вместо байтов.
   */
  const type = (data.type || '').toLowerCase()
  if (type.indexOf('application/json') === 0) {
    const text = await data.text()
    const url = (JSON.parse(text) || {}).s3url
    if (!url) throw new Error('платформа не дала ни файла, ни ссылки')
    // К хранилищу — без токена и без переадресаций; CORS хранилища может это и запретить.
    const s3 = await fetch(url, { credentials: 'omit', redirect: 'error' })
    if (!s3.ok) throw new Error(`хранилище ответило ${s3.status}`)
    return s3.blob()
  }
  return data
}

/**
 * Передать мосту байты всех вложений — ДО старта прогона (§2, §18 п. 32): передача не должна
 * съедать срок токена агента.
 *
 * `items` — описания, подтверждённые сервером, плюс `file` (то, что человек выбрал), если байты
 * ещё живы в браузере. Не удалось передать хотя бы один — задание НЕ уходит: отправить его без
 * файла значило бы получить ответ неизвестно о чём.
 */
export async function putAttachmentsToBridge(items) {
  const placed = []
  // Строго по очереди, а не параллельно: у моста один предел суммы и общий счётчик частоты —
  // десять одновременных отправок упёрлись бы в него раньше, чем в диск.
  for (let i = 0; i < items.length; i += 1) {
    const meta = items[i]
    let blob = meta.file || null
    if (!blob) {
      try {
        // eslint-disable-next-line no-await-in-loop
        blob = await downloadAttachment(meta)
      } catch (e) {
        throw new Error(`Файл «${meta.name}» не передан мосту: ${e.message}`)
      }
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const answer = unwrap(await sendFileToBridge(meta, blob))
      if (!answer || answer.stored !== true) throw new Error('мост не подтвердил приём')
      placed.push(meta)
    } catch (e) {
      throw new Error(`Файл «${meta.name}» не передан мосту: ${e.message}`)
    }
  }
  return placed
}

/**
 * PROP-472.3: сообщить Cubus о завершившемся прогоне локального Клода.
 *
 * Отчёт идёт в бэкенд, а не через мост: строку журнала пишет сервер, он же ставит домен,
 * автора и время. Мост в Cubus не ходит по построению (BRG-445), и заводить ему такой путь
 * ради журнала значило бы превращать грант на ПАПКУ в доступ к ДАННЫМ Cubus.
 */
export function reportLocalRun(payload) {
  return axiosIns.post('/api/ai/activity', payload)
}

/** Готовность Клода. Идёт через то же окно: прямой запрос браузер не пропустит. */
export function bridgeHealth() { return relay('/health').then(unwrap) }

/**
 * BFILE-561 (ТЗ моста §8.3): чем кончился прогон, поток которого оборвался.
 *
 * Обрыв потока — это не «прогона не было»: мост помнит последние итоги, и отчитаться по ним
 * лучше, чем оставить в журнале обрыв без исхода, а след — незакрытым. Итог мост отдаёт с тем
 * же `finishedAt`, что ушёл бы в событии, поэтому момент в отчёте не расходится.
 */
export function bridgeResult(sessionId) {
  return relay(`/result/${encodeURIComponent(sessionId)}`).then(unwrap)
}

/**
 * Контракт ответа для локального Клода.
 *
 * Раннер отдаёт блоки сам (PROP-423), а мост возвращает просто текст — поэтому здесь мы просим
 * ту же форму, а разбирает её фронт тем же запасным путём, что и для старого раннера. Правила
 * вёрстки страницы те же, что в промпте раннера: страницу человек сохраняет в PDF, а тот
 * собирается движком уровня CSS 2.1.
 */
export function answerContract(present) {
  /**
   * BUG-439: формы адресов названы ЯВНО.
   *
   * Раньше здесь стояло «дай ссылку путём от корня вида `/cubus/...`» — и ни одной конкретной
   * формы. Агент честно выполнял инструкцию и сочинял правдоподобное: живой прогон дал пять
   * ссылок и ни одной рабочей (`/cubus/object`, `/cubus/entity`, `/cubus/attribute`, …), потому
   * что маршрут объекта — `/cubus/object/:objectid`, а без параметра не совпадает ни с чем.
   *
   * Перечень тот же, что у раннера (`prompts/_common.md`): у серверного агента ссылки рабочие
   * ровно потому, что формы ему названы. Два агента в одном окне не должны вести себя по-разному.
   */
  const base = [
    'Ты отвечаешь человеку в окне чата платформы Cubus. Отвечай по существу и на русском.',
    'Ссылки внутрь Cubus давай ТОЛЬКО в этих формах, путём от корня:',
    '- объект: `/cubus/object/86` или `/cubus/object/cubecashflow` — годится и числовой id, и sid;',
    '- запись: `/cubus/recordpage/86/1204` — сперва объект, затем запись;',
    '- списки: `/cubus/objects`, `/cubus/tables`, `/cubus/refs`, `/cubus/cubes`, `/cubus/reports`,',
    '`/cubus/procedures`, `/cubus/imports`, `/cubus/mounts`.',
    'Других адресов не выдумывай: несуществующий путь открывается страницей 404.',
    'Идентификатор бери ТОЛЬКО из ответа инструмента; не уверен в нём — назови объект словами,',
    'без ссылки. Внешние адреса ссылкой не оформляй.',
  ]
  if (!present) return base.join(' ')
  return base.concat([
    '',
    'Ответ оформи САМОСТОЯТЕЛЬНОЙ HTML-страницей целиком внутри блока ```html … ``` —',
    'со встроенным CSS и без внешних ресурсов. Перед блоком дай одну короткую строку-резюме',
    'обычным текстом: её человек увидит в переписке, а страницу — в основной области.',
    'Вёрстка только CSS 2.1: таблицы, блоки, float; без flexbox, grid и JavaScript;',
    'шрифт не задавай; картинки только data:-URI. Эту страницу человек сохраняет в PDF,',
    'а PDF собирает движок, который flex и grid не понимает.',
  ]).join('\n')
}

/**
 * Спросить локального Клода. Возвращает `{ text, sessionId }`.
 *
 * `sessionId` возвращается мостом и передаётся обратно следующим запросом — так разговор
 * продолжается, а не начинается заново на каждой реплике. Проект не передаём: доступ выдан на
 * одну папку, и мост сам знает, какую.
 */
export async function askBridge({
  prompt, sessionId = '', present = false, mcp = null, requestId = '',
}) {
  const body = { prompt, systemPrompt: answerContract(present) }
  if (sessionId) body.sessionId = sessionId
  if (requestId) body.requestId = requestId
  if (mcp) body.mcp = mcp
  const data = unwrap(await relay('/ask', { method: 'POST', body }))
  const result = (data && data.result) || ''
  if (data && data.isError) throw new Error(result || 'Клод вернул ошибку.')
  return { text: result, sessionId: (data && data.sessionId) || sessionId }
}

/**
 * Ждём подтверждения вложений: либо оно придёт событием `accepted`, либо прогон закончится
 * раньше (мост отказал). Отдельная функция потому, что ждать надо ДВА исхода сразу — иначе
 * отказ старого моста повис бы до конца прогона.
 */
function waitForAccepted(answer, read) {
  return new Promise(resolve => {
    let done = false
    const finish = v => { if (!done) { done = true; resolve(v) } }
    const tick = setInterval(() => {
      const v = read()
      if (v !== null) { clearInterval(tick); finish(v) }
    }, 50)
    answer.then(() => { clearInterval(tick); finish(read()) }, () => { clearInterval(tick); finish(read()) })
  })
}

/**
 * BRG-435: прервать незавершённый запрос.
 *
 * Мост снимает и выполняющийся запуск, и ОЖИДАЮЩИЙ в очереди — второе важно не меньше:
 * процесса ещё нет, но человек уже ждёт. Сам `/ask` ответит на это отказом `CANCELLED`.
 */
export function cancelBridge(requestId) {
  return relay('/cancel', { method: 'POST', body: { requestId } }).then(unwrap)
}

/**
 * BRG-438: тот же вопрос, но с вехами хода работы.
 *
 * Отличается от `askBridge` только тем, что идёт в `/ask/stream` и по дороге зовёт `onEvent`.
 * Формат вех — сырые события Claude Code: разбирать их дело показывающей стороны, здесь мы
 * ничего не толкуем, чтобы не пришлось править сервис на каждое новое событие CLI.
 */
export async function askBridgeStream({
  prompt, sessionId = '', present = false, mcp = null, requestId = '', onEvent = null,
  jobId = '', attachments = null, memoryRun = false, resultFiles = null,
}) {
  const body = { prompt, systemPrompt: answerContract(present) }
  if (sessionId) body.sessionId = sessionId
  if (requestId) body.requestId = requestId
  if (mcp) body.mcp = mcp
  /**
   * BFILE-560 (ТЗ моста §13 п. 3): ВЛОЖЕНИЯ ОТПРАВЛЯЮТСЯ ТОЛЬКО ТОМУ, КТО ИХ ПОНИМАЕТ.
   *
   * Старый мост неизвестные поля тела молча игнорирует: получив `attachments`, он запустится
   * БЕЗ файлов и ответит как ни в чём не бывало. Проверка здесь — первая и главная защита;
   * вторая (подтверждение в `accepted`) стоит ниже и ловит уже нашу собственную ошибку.
   */
  const withFiles = !!jobId && Array.isArray(attachments) && attachments.length > 0 && bridgeCanFiles()
  /**
   * BFILE-564: номер прогона уходит мосту в КАЖДОМ сообщении, а не только с файлами.
   * «Посмотри, потом запомни» пишет память вторым сообщением, в котором файлов нет, — и агенту
   * нужен номер (`CUBUS_JOB_IDS`), а мосту — знать, что это прогон с памятью. Старый мост
   * незнакомые поля пропускает молча, так что номер без файлов ему не вредит.
   */
  if (jobId) body.jobId = jobId
  // Прогон с памятью — без настроек человека: правила «Запомни» не должны спорить с его скиллами.
  if (jobId && memoryRun && bridgeCanMemory()) body.memoryRun = true
  /**
   * RFILE-630 (ТЗ «Мост: файлы результата» §2 п. 2, §8, §19 п. 1): поле `resultFiles` — только мосту с
   * возможностью. Пределы и форматы — платформенные из `local/start`; файлы у моста — только при фоновой
   * приёмке, иначе `{enabled: false}`, и агенту мост скажет «недоступны». Без номера прогона — тоже нет.
   */
  if (bridgeCanResultFiles()) {
    const rf = resultFiles || {}
    body.resultFiles = jobId && rf.enabled === true && rf.intake === 'background'
      ? {
        enabled: true, maxsize: rf.maxsize, maxcount: rf.maxcount, maxtotal: rf.maxtotal, formats: rf.formats,
      }
      : { enabled: false }
  }
  if (withFiles) {
    body.attachments = attachments.map(a => ({
      id: a.id, name: a.name, size: a.size, sha256: a.sha256,
    }))
  }

  let confirmed = null
  let placed = []
  // RFILE-630 (§8 п. 3): мост подтверждает поле в `accepted`; без подтверждения событие не ждём и мест не заводим.
  let resultConfirmed = false
  let described = null
  const watch = (name, data) => {
    // Мост называет распознанные вложения в `accepted` и разложенные — в `attachments_placed`.
    if (name === 'accepted') {
      confirmed = Array.isArray(data && data.attachments) ? data.attachments : null
      resultConfirmed = !!(data && data.resultFiles === true)
    }
    // RFILE-630 (§2 п. 5): описание файлов прогона — до `done`.
    if (name === 'result_files' && data) {
      described = {
        requestId: String(data.requestId || ''),
        files: Array.isArray(data.files) ? data.files : [],
        failed: Array.isArray(data.failed) ? data.failed : [],
      }
    }
    /**
     * BFILE-561: номера, которые мост ДЕЙСТВИТЕЛЬНО положил во входящие. В отчёт идут именно
     * они, а не то, что мы отправили: иначе отчёт подтверждал бы собственное намерение, а не
     * факт, и след закрывался бы «файлы обработаны» там, где их не было.
     */
    if (name === 'attachments_placed') placed = Array.isArray(data && data.attachments) ? data.attachments : []
    if (onEvent) onEvent(name, data)
  }

  const answer = relay('/ask/stream', { method: 'POST', body, onEvent: watch })

  if (withFiles) {
    /**
     * Вторая линия (§13 п. 3). `accepted` приходит ДО запуска процесса, но мост раскладывает
     * папку и занимает слот синхронно — при свободном слоте модель успевает сделать первый ход.
     * Поэтому это не предотвращение запуска, а его остановка: прогон снимается, человеку
     * говорится правда, а отчёт без `attachments_placed` закроет след `failed`.
     */
    const seen = await waitForAccepted(answer, () => confirmed)
    if (seen === null) {
      try { await cancelBridge(requestId) } catch (e) { /* снимать уже нечего */ }
      throw new Error('Мост не принял вложения — обновите мост: claude-bridge update. Задание снято.')
    }
  }

  const data = unwrap(await answer)
  const result = (data && data.result) || ''
  if (data && data.isError) throw new Error(result || 'Клод вернул ошибку.')
  return {
    text: result,
    sessionId: (data && data.sessionId) || sessionId,
    usage: readUsage(data),
    attachmentsPlaced: placed,
    // RFILE-630: описанные мостом файлы результата — только при подтверждённом поле.
    resultFiles: resultConfirmed && described ? described : null,
    // BFILE-555/561: момент окончания называет МОСТ. Своё время здесь означало бы часы другой
    // машины, а сервер сверяет момент с окном следа.
    finishedAt: Number(data && data.finishedAt) || 0,
  }
}

/**
 * BRG-435: номер запроса, который панель назначает САМА.
 *
 * Серверный `requestId` для этого не годится: ответ `/ask` единый, и заголовки `X-Bridge-*`
 * физически не могут прийти раньше тела (разобрано в `BRG-346`) — то есть к моменту, когда
 * номер стал бы известен, отменять уже нечего. Только ASCII: номер уезжает в HTTP-заголовок.
 */
export function newRequestId() {
  const c = window.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Вызов тула снимка. Токен берём тот же, что и для инструментов агента, и на один запрос.
 *
 * ⚠️ BUG-674: `fetch`, А НЕ ОБЩИЙ `axiosIns` — та же ловушка, что у `uploadResultFileToCubus`
 * выше: перехватчик `jwtService` безусловно ставит `Authorization` токеном ВЕБ-СЕССИИ
 * и затирает переданный агентский. На инстансах с `apitoken.require-for-mcp = true` (стенд, kbtu,
 * app) `/mcp` отказывает веб-сессии, и карта домена не доходила до моста нигде, кроме локального
 * Cubus, где флаг выключен (там и принимался BRG-454).
 */
async function callDomainSnapshot(knownBuild) {
  const tokenRes = await axiosIns.post('/api/mcptoken', {})
  const token = tokenRes && tokenRes.data && tokenRes.data.token
  if (!token) throw new Error('Cubus не выдал доступ для чтения структуры домена.')
  const args = typeof knownBuild === 'number' ? { known_build: knownBuild } : {}
  const url = `${String(getConfig() || '').replace(/\/+$/, '')}/mcp`
  const resp = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0', id: 1, method: 'tools/call', params: { name: 'cubus_domain_snapshot', arguments: args },
    }),
    credentials: 'omit',
  })
  if (!resp.ok) throw new Error(`Cubus ответил ${resp.status} на запрос снимка структуры.`)
  const data = await resp.json()
  if (data && data.error) throw new Error(data.error.message || 'Cubus отказал в снимке структуры.')
  const content = data && data.result && data.result.content
  const raw = Array.isArray(content) && content[0] ? content[0].text : ''
  if (data && data.result && data.result.isError) throw new Error(String(raw).slice(0, 300))
  try {
    return JSON.parse(raw)
  } catch (e) {
    throw new Error('ответ инструмента не разбирается как JSON')
  }
}

/** Скачать карту по одноразовому токену. Форма ответа зависит от хранилища файлов, поэтому обе. */
async function downloadSnapshot(tokens) {
  const token = Array.isArray(tokens) && tokens[0] ? tokens[0].token : ''
  if (!token) throw new Error('токена скачивания в ответе нет')
  const res = await axiosIns.post('/api/download', { param: { downloadtoken: token } })
  const data = res && res.data
  // Файл в S3 отдаётся ссылкой, локальный — телом. Разбирать надо оба: иначе карта теряется на
  // ровном месте там, где хранилище настроено иначе.
  if (data && typeof data === 'object' && data.s3url) {
    const file = await fetch(data.s3url)
    if (!file.ok) throw new Error(`хранилище ответило ${file.status}`)
    return file.text()
  }
  return typeof data === 'string' ? data : ''
}

/**
 * BRG-454: карта структуры домена — страница соединяет бэк и мост.
 *
 * ⚠️ ПОЧЕМУ ЭТО ДЕЛАЕТ СТРАНИЦА. Мост в Cubus не ходит и ходить не должен: у него грант на ПАПКУ,
 * а не на данные домена (та же причина, по которой заметки памяти носит страница, — `BRG-445`).
 * Значит соединить две половины может только тот, у кого есть и доступ к домену, и связь с
 * мостом, то есть эта страница.
 *
 * КАК УСТРОЕН РАЗГОВОР. У моста спрашиваем номер сборки структуры, при котором снята его карта
 * (`GET /domain`), идём с этим номером в Cubus (`cubus_domain_snapshot`, `PROP-451`) и несём
 * ответ обратно: `up_to_date` — только отметку, новая карта — вместе с телом.
 *
 * ⚠️ НЕ В ГОРЯЧЕМ ПУТИ ОТВЕТА. Зовётся при подключении, а не перед вопросом: человек не должен
 * ждать карту домена, чтобы задать вопрос. И ни один отказ здесь не имеет права сорвать чат —
 * функция ничего не бросает, а возвращает исход строкой.
 */
export async function syncDomainSnapshot(domainSid) {
  const sid = String(domainSid || '').trim()
  if (!sid) return { action: 'skipped', reason: 'домен сессии неизвестен' }
  if (!isBridgeConnected()) return { action: 'skipped', reason: 'мост не подключён' }

  let known = null
  try {
    const state = unwrap(await relay('/domain'))
    const d = (state && state.domain) || {}
    // ⚠️ Номер ЧУЖОГО домена в качестве `known_build` — это ложное совпадение: один мост ходит в
    // разные домены, и «сборка 17» из соседнего домена означала бы «карта свежая» там, где её нет.
    known = d.sid === sid && typeof d.build === 'number' ? d.build : null
  } catch (e) {
    // Мост не запущен, доступ истёк, окно закрыли — всё это не ошибка чата.
    return { action: 'skipped', reason: e.message }
  }

  let answer = null
  try {
    answer = await callDomainSnapshot(known)
  } catch (e) {
    return { action: 'failed', reason: e.message }
  }
  if (!answer || typeof answer.build !== 'number') {
    // Домен раскатан до счётчика (`PROP-449`): номера нет, и сравнивать нечего. Это не поломка,
    // но и молчать нельзя — иначе «карта не появилась» выглядит как отказ моста.
    return { action: 'skipped', reason: 'у домена нет номера сборки структуры — нужен деплой домена' }
  }

  if (answer.status === 'up_to_date') {
    try {
      const res = unwrap(await relay('/domain', { method: 'POST', body: { sid, build: answer.build } }))
      return { action: res.action || 'confirmed', build: answer.build }
    } catch (e) {
      return { action: 'failed', reason: e.message }
    }
  }

  let text = typeof answer.snapshot === 'string' ? answer.snapshot : ''
  if (!text) {
    // Большая карта приезжает НЕ телом, а токеном скачивания (`PROP-451`). Не отработать это
    // значило бы положить мосту пустоту ровно на тех доменах, ради которых карта и нужна.
    try {
      text = await downloadSnapshot(answer.downloadtokens)
    } catch (e) {
      return { action: 'failed', reason: `карта пришла файлом, и скачать его не вышло: ${e.message}` }
    }
  }
  if (!text) return { action: 'failed', reason: 'Cubus вернул пустую карту' }

  try {
    const res = unwrap(await relay('/domain', { method: 'POST', body: { sid, build: answer.build, snapshot: text } }))
    return { action: res.action || 'written', build: answer.build, bytes: (res.domain && res.domain.bytes) || 0 }
  } catch (e) {
    return { action: 'failed', reason: e.message }
  }
}

/**
 * BRG-432: инструменты Cubus для локального Клода.
 *
 * ⚠️ ТОКЕН БЕРЁТСЯ ПЕРЕД КАЖДЫМ ЗАПРОСОМ, а не один раз при подключении. Это не расточительство:
 * бэкенд выдаёт доступ на минуты (BRG-430), и обращения его НЕ продлевают — значит запасённый
 * заранее токен к моменту второго вопроса уже был бы мёртв. Дешевле попросить новый, чем держать
 * живым весь разговор доступ к данным домена у процесса, который выполняет команды на машине.
 *
 * Прежде здесь лежала `mcpSnippet` — она собирала текст `.mcp.json`, чтобы человек положил файл
 * себе в папку руками. Её никто не вызывал, и работать она всё равно не могла: при `claude -p`
 * проектный MCP-сервер требует одобрения в интерактивном режиме, которого там нет.
 */
const mcpConfigOf = token => ({
  mcpServers: {
    cubus: {
      type: 'http',
      url: `${String(getConfig() || '').replace(/\/+$/, '')}/mcp`,
      headers: { Authorization: `Bearer ${token}` },
    },
  },
})

/**
 * BFILE-559 (ТЗ моста §7.1, §13 п. 5): СТАРТ ЛОКАЛЬНОГО ПРОГОНА.
 *
 * Раньше панель просто брала доступ в `/api/mcptoken`. Теперь она СТАРТУЕТ ПРОГОН: сервер выдаёт
 * `job_id`, помечает им выданную сессию агента и — если к сообщению приложены файлы — открывает
 * строку следа. Без номера платформе нечем связать запись памяти и отчёт с этим прогоном: ни
 * закрыть след, ни учесть квоту, ни сказать потом, что именно агент читал.
 *
 * ⚠️ ЗАПАСНОЙ ПУТЬ ОСТАЁТСЯ. Бэкенд бывает старее фронта (§13 п. 5): нет точки — берём доступ
 * как раньше и работаем без номера прогона. Вложения при этом не отправляются вовсе — отправить
 * их было бы обещанием, которое некому выполнить.
 */
export async function startLocalRun(fileIds = []) {
  try {
    const res = await axiosIns.post('/api/agent/local/start', { files: fileIds })
    const d = (res && res.data) || {}
    const token = d.mcp && d.mcp.token
    if (!token) throw new Error('Cubus не выдал доступ для локального агента.')
    return {
      mcp: mcpConfigOf(token),
      jobId: d.job_id || '',
      memory: d.memory === true,
      // Описания файлов подтверждает СЕРВЕР: отпечаток и размер берутся отсюда, а не из браузера.
      files: Array.isArray(d.files) ? d.files : [],
      // RFILE-630 (ТЗ «Мост: файлы результата» §6.1): признак файлов результата; прежний Cubus его не шлёт.
      resultFiles: d.result_files && typeof d.result_files === 'object' ? d.result_files : null,
      legacy: false,
    }
  } catch (e) {
    const status = e && e.response && e.response.status
    // 404 — бэкенд старее фронта. Прочие отказы (роль, выключатель, пределы) отдаём как есть.
    if (status !== 404) {
      const msg = (e && e.response && e.response.data && e.response.data.error) || (e && e.message)
      const err = new Error(msg || 'Cubus не выдал доступ для локального агента.')
      // PROP-754: отказ «нет подтверждения моста» панель узнаёт по имени поля и открывает окно подтверждения
      err.field = e && e.response && e.response.data && e.response.data.field
      throw err
    }
    const res = await axiosIns.post('/api/mcptoken', {})
    const token = res && res.data && res.data.token
    if (!token) throw new Error('Cubus не выдал доступ для локального агента.')
    return {
      mcp: mcpConfigOf(token), jobId: '', memory: false, files: [], resultFiles: null, legacy: true,
    }
  }
}
